import type {
  ProjectSummary,
  ProjectExportArtifactV1,
  ProjectExportEventV1,
  ProjectReadinessV1,
  ProjectCostTelemetryResponse,
  ScheduleCategory,
  ScheduleEntry,
  ScheduleEntryStatus,
  ScheduleKind,
  ScheduleSeverity,
  SpecPolicyFlagScope,
  SpecPolicyFlagSet
} from "@automatry/schemas";

import { withAuthHeaders } from "../browser/apiAuthHeaders";
import type {
  CreateProjectDraft,
  CreateProjectMetaDraft,
  UploadedDocumentDraft
} from "../features/create/createDraft";
import type { OnboardingStepId } from "../shell";
import type { ProjectsSummaryFixture } from "../viewmodel/projectsDashboardAdapter";

export type ProjectStatus =
  | "draft"
  | "inputs"
  | "requirements"
  | "specifying"
  | "specified"
  | "partial"
  | "failed";

export type ProjectSource = "created" | "seed";

export type SpecJobStatus =
  | "running"
  | "succeeded"
  | "failed"
  | "partial"
  | "interrupted";

export interface ProjectRecordResponse {
  id: string;
  orgId: string;
  ownerId: string | null;
  name: string;
  status: ProjectStatus;
  source: ProjectSource;
  onboardingStep: OnboardingStepId | null;
  location: string | null;
  client: string | null;
  siteAddress: string | null;
  consultant: string | null;
  mainContractor: string | null;
  /**
   * Free-text project notes (tender-details dialog). Named distinctly from
   * the top-level `ProjectDetailResponse.notes` array (structured
   * clarification/requirement notes) to avoid confusing the two. Optional
   * (like `policyFlagSet` above) so existing fixtures across the app that
   * predate this field don't all need updating for a field they never used.
   */
  projectNotes?: string | null;
  policyFlagSet?: SpecPolicyFlagSet | null;
  summary: ProjectSummary | null;
  activeJobId: string | null;
  activeSpecId: string | null;
  createdAt: string;
  updatedAt: string;
  revision: number;
}

export interface ProjectDocumentResponse extends Omit<
  UploadedDocumentDraft,
  "httpStatus" | "error" | "parsedAt" | "extraction"
> {
  projectId: string;
  httpStatus: number | null;
  extraction: UploadedDocumentDraft["extraction"] | null;
  error: string | null;
  parsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetailSpecJobResponse {
  id: string;
  projectId: string | null;
  status: SpecJobStatus;
  phase: "specifying" | "aggregating" | "done";
  cancelled: boolean;
  progress: unknown;
  request: unknown;
  error: { code: string; message: string } | null;
  resultId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetailSpecResultResponse {
  id: string;
  projectId: string;
  jobId: string | null;
  specSnapshotId: string;
  result: unknown;
  createdAt: string;
}

export type ProjectNoteKind =
  | "clarification"
  | "exclusion"
  | "commissioning"
  | "requirement-amendment";

export type ProjectNoteAuthorType = "user" | "ai";
export type ProjectNoteStatus =
  | "active"
  | "conflict_flagged"
  | "promoted_to_requirement";

export interface ProjectNoteSubjectResponse {
  id: string;
  label: string;
  kind?: string;
}

export interface ProjectNoteAiProvenanceResponse {
  why: string;
  where: string;
  toolName?: string;
  model?: string;
  sourceMessageId?: string;
}

export interface ProjectNoteDismissedConflictPairResponse {
  noteIds: [string, string];
  contentFingerprints: [string, string];
}

export interface ProjectNoteResponse {
  id: string;
  projectId: string;
  kind: ProjectNoteKind;
  noteKind: ProjectNoteKind;
  text: string;
  authorType: ProjectNoteAuthorType;
  status: ProjectNoteStatus;
  subjectRef?: ProjectNoteSubjectResponse;
  aiProvenance?: ProjectNoteAiProvenanceResponse;
  conflictsWith: string[];
  dismissedConflictPairs?: ProjectNoteDismissedConflictPairResponse[];
  promotedRequirementId?: string;
  lastEditedBy?: ProjectNoteAuthorType;
  createdAt: string;
  updatedAt: string;
}

// Populated by the server from the same tender-intake repository lookup the
// Tender ingestion screen uses to resolve its own pack (see
// resolveProjectTenderScope in apps/server/src/projectsApi.ts). `null` means
// the server checked and found no active finalized pack (or could not check)
// -- it is never fabricated client-side. Optional/undefined only for older
// fixtures and callers that predate this field; treat it the same as `null`.
export interface ProjectTenderScopeResponse {
  packId: string;
  status: string;
}

export interface ProjectDetailResponse {
  project: ProjectRecordResponse;
  draft: CreateProjectDraft | null;
  documents: ProjectDocumentResponse[];
  notes?: ProjectNoteResponse[];
  scheduleEntries?: ScheduleEntry[];
  activeJob?: ProjectDetailSpecJobResponse;
  specResult?: ProjectDetailSpecResultResponse;
  costRollup?: ProjectCostTelemetryResponse;
  readiness?: ProjectReadinessV1;
  tenderScope?: ProjectTenderScopeResponse | null;
}

const PROJECTS_ENDPOINT = "/api/projects";

export async function listProjects(): Promise<ProjectsSummaryFixture> {
  return requestJson<ProjectsSummaryFixture>(PROJECTS_ENDPOINT, {
    method: "GET",
    headers: { accept: "application/json" }
  });
}

export async function createProject(input: {
  name: string;
  projectMeta?: Partial<CreateProjectMetaDraft>;
}): Promise<{ id: string; updatedAt: string; revision: number }> {
  const payload = await requestJson<{
    id?: unknown;
    updatedAt?: unknown;
    revision?: unknown;
  }>(PROJECTS_ENDPOINT, {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (typeof payload.id !== "string" || payload.id.length === 0) {
    throw new Error("Projects API returned an invalid create-project response.");
  }

  if (typeof payload.updatedAt !== "string" || payload.updatedAt.length === 0) {
    throw new Error(
      "Projects API returned an invalid create-project version response."
    );
  }

  if (typeof payload.revision !== "number" || !Number.isInteger(payload.revision)) {
    throw new Error(
      "Projects API returned an invalid create-project revision response."
    );
  }

  return { id: payload.id, updatedAt: payload.updatedAt, revision: payload.revision };
}

export async function getProject(
  id: string,
  options: { signal?: AbortSignal } = {}
): Promise<ProjectDetailResponse> {
  return requestJson<ProjectDetailResponse>(projectEndpoint(id), {
    method: "GET",
    headers: { accept: "application/json" },
    ...(options.signal ? { signal: options.signal } : {})
  });
}

export async function getProjectReadiness(id: string): Promise<ProjectReadinessV1> {
  return requestJson<ProjectReadinessV1>(`${projectEndpoint(id)}/readiness`, {
    method: "GET",
    headers: { accept: "application/json" }
  });
}

/**
 * Lightweight partial project-meta update (tender-details dialog):
 * name/client/site/notes only, no `expectedRevision` precondition -- see
 * `handleUpdateProjectMeta` in projectsApi.ts. Only supplied keys change;
 * an empty string clears a field.
 */
export async function updateProjectMeta(
  id: string,
  body: {
    name?: string;
    client?: string;
    siteAddress?: string;
    notes?: string;
  }
): Promise<{ project: ProjectRecordResponse }> {
  return requestJson<{ project: ProjectRecordResponse }>(projectEndpoint(id), {
    method: "PATCH",
    headers: {
      accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
}

export async function saveProject(
  id: string,
  body: {
    draft: CreateProjectDraft;
    policyFlagSet?: SpecPolicyFlagSet | null;
    documents?: UploadedDocumentDraft[];
    onboardingStep: OnboardingStepId;
    projectMeta?: Partial<CreateProjectMetaDraft>;
    expectedRevision: number;
  }
): Promise<ProjectDetailResponse> {
  return requestJson<ProjectDetailResponse>(projectEndpoint(id), {
    method: "PUT",
    headers: {
      accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
}

export async function updateCompareMatrixCell(
  projectId: string,
  body: {
    groupId: "takeoffs" | "controls";
    kind: string;
    apartmentTypeId: string;
    value: number;
    reason?: string | undefined;
    expectedRevision: number;
  }
): Promise<ProjectDetailResponse> {
  return requestJson<ProjectDetailResponse>(
    projectCompareMatrixCellEndpoint(projectId),
    {
      method: "PATCH",
      headers: {
        accept: "application/json",
        "content-type": "application/json"
      },
      body: JSON.stringify(body)
    }
  );
}

export async function createProjectNote(
  projectId: string,
  body: {
    kind: ProjectNoteKind;
    text: string;
    subjectRef?: ProjectNoteSubjectResponse;
    authorType?: ProjectNoteAuthorType;
    aiProvenance?: ProjectNoteAiProvenanceResponse;
    conflictsWith?: string[];
    dismissedConflictPairs?: ProjectNoteDismissedConflictPairResponse[];
    promotedRequirementId?: string;
    lastEditedBy?: ProjectNoteAuthorType;
  }
): Promise<ProjectNoteResponse> {
  const payload = await requestJson<{ note: ProjectNoteResponse }>(
    projectNotesEndpoint(projectId),
    {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json"
      },
      body: JSON.stringify(body)
    }
  );

  return payload.note;
}

export async function updateProjectNote(
  projectId: string,
  noteId: string,
  body: {
    kind?: ProjectNoteKind;
    text?: string;
    subjectRef?: ProjectNoteSubjectResponse | null;
    authorType?: ProjectNoteAuthorType;
    status?: ProjectNoteStatus;
    aiProvenance?: ProjectNoteAiProvenanceResponse | null;
    conflictsWith?: string[];
    dismissedConflictPairs?: ProjectNoteDismissedConflictPairResponse[];
    promotedRequirementId?: string | null;
    lastEditedBy?: ProjectNoteAuthorType | null;
    resolveConflict?: true;
  }
): Promise<ProjectNoteResponse> {
  const payload = await requestJson<{ note: ProjectNoteResponse }>(
    projectNoteEndpoint(projectId, noteId),
    {
      method: "PATCH",
      headers: {
        accept: "application/json",
        "content-type": "application/json"
      },
      body: JSON.stringify(body)
    }
  );

  return payload.note;
}

export async function resolveProjectNoteConflict(
  projectId: string,
  noteId: string
): Promise<ProjectNoteResponse> {
  return updateProjectNote(projectId, noteId, { resolveConflict: true });
}

export async function deleteProjectNote(
  projectId: string,
  noteId: string
): Promise<void> {
  await requestJson<unknown>(projectNoteEndpoint(projectId, noteId), {
    method: "DELETE",
    headers: { accept: "application/json" }
  });
}

export async function createProjectScheduleEntry(
  projectId: string,
  body: {
    kind: ScheduleKind;
    category: ScheduleCategory;
    severity: ScheduleSeverity;
    affectedApartmentTypeIds?: string[];
    scope?: SpecPolicyFlagScope;
    bodyText: string;
    status?: Extract<ScheduleEntryStatus, "proposed" | "accepted">;
  }
): Promise<{ scheduleEntry: ScheduleEntry; scheduleEntries: ScheduleEntry[] }> {
  return requestJson<{
    scheduleEntry: ScheduleEntry;
    scheduleEntries: ScheduleEntry[];
  }>(projectSchedulesEndpoint(projectId), {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
}

export async function generateProjectScheduleEntries(
  projectId: string,
  body: { source?: "policy-flags" | "specification-notes" | "all" } = {}
): Promise<{
  provider: { id: string; model: string };
  source: "policy-flags" | "specification-notes" | "all";
  trace?: Array<{ id: string; label: string; status: string; detail: string }>;
  harvestedEvidenceCount?: number;
  proposals: ScheduleEntry[];
  rejectedProposals: Array<{
    id: string;
    kind: ScheduleKind;
    bodyText: string;
    sourceFlagId?: string;
    sourceFlagFamilyId?: string;
    sourceNoteId?: string;
    sourceNoteFingerprint?: string;
    error: string;
    issues: unknown[];
  }>;
  scheduleEntries: ScheduleEntry[];
}> {
  return requestJson<{
    provider: { id: string; model: string };
    source: "policy-flags" | "specification-notes" | "all";
    trace?: Array<{ id: string; label: string; status: string; detail: string }>;
    harvestedEvidenceCount?: number;
    proposals: ScheduleEntry[];
    rejectedProposals: Array<{
      id: string;
      kind: ScheduleKind;
      bodyText: string;
      sourceFlagId?: string;
      sourceFlagFamilyId?: string;
      sourceNoteId?: string;
      sourceNoteFingerprint?: string;
      error: string;
      issues: unknown[];
    }>;
    scheduleEntries: ScheduleEntry[];
  }>(projectSchedulesGenerateEndpoint(projectId), {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
}

export async function updateProjectScheduleEntry(
  projectId: string,
  entryId: string,
  body: {
    kind?: ScheduleKind;
    category?: ScheduleCategory;
    severity?: ScheduleSeverity;
    affectedApartmentTypeIds?: string[];
    scope?: SpecPolicyFlagScope;
    bodyText?: string;
    status?: ScheduleEntryStatus;
    restoreForReapproval?: true;
  }
): Promise<{ scheduleEntry: ScheduleEntry; scheduleEntries: ScheduleEntry[] }> {
  return requestJson<{
    scheduleEntry: ScheduleEntry;
    scheduleEntries: ScheduleEntry[];
  }>(projectScheduleEndpoint(projectId, entryId), {
    method: "PATCH",
    headers: {
      accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(body)
  });
}

export async function deleteProjectScheduleEntry(
  projectId: string,
  entryId: string
): Promise<{ scheduleEntry: ScheduleEntry; scheduleEntries: ScheduleEntry[] }> {
  return requestJson<{
    scheduleEntry: ScheduleEntry;
    scheduleEntries: ScheduleEntry[];
  }>(projectScheduleEndpoint(projectId, entryId), {
    method: "DELETE",
    headers: { accept: "application/json" }
  });
}

export async function deleteProject(id: string): Promise<void> {
  await requestJson<unknown>(projectEndpoint(id), {
    method: "DELETE",
    headers: { accept: "application/json" }
  });
}

export async function waiveProjectReadinessEntry(
  projectId: string,
  body: {
    entryId: string;
    expectedRevision: number;
    reason: string;
    issueProfile?: "priced" | "technical_only";
  }
): Promise<{ ok: true; readiness: ProjectReadinessV1 }> {
  return requestJson(`${projectEndpoint(projectId)}/readiness/waivers`, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(body)
  });
}

export async function answerProjectReadinessEntry(
  projectId: string,
  body: {
    entryId: string;
    expectedRevision: number;
    answerReference: string;
    issueProfile?: "priced" | "technical_only";
  }
): Promise<{ ok: true; readiness: ProjectReadinessV1 }> {
  return requestJson(`${projectEndpoint(projectId)}/readiness/answers`, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(body)
  });
}

export interface ProjectExportRequest {
  mode: "internal_draft" | "client_issue";
  issueProfile: "priced" | "technical_only";
  expectedReadinessRevision: number;
  expectedSourceVersionHash: string;
  artifacts?: ProjectExportArtifactV1[];
  draftWatermarked?: boolean;
}

export async function submitProjectExport(
  projectId: string,
  body: ProjectExportRequest,
  idempotencyKey?: string
): Promise<{
  ok: true;
  allowed?: true;
  event?: ProjectExportEventV1;
  readiness: ProjectReadinessV1;
}> {
  return requestJson(`${projectEndpoint(projectId)}/exports`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      ...(idempotencyKey ? { "idempotency-key": idempotencyKey } : {})
    },
    body: JSON.stringify(body)
  });
}

const projectEndpoint = (id: string): string =>
  `${PROJECTS_ENDPOINT}/${encodeURIComponent(id)}`;
const projectNotesEndpoint = (projectId: string): string =>
  `${projectEndpoint(projectId)}/notes`;
const projectNoteEndpoint = (projectId: string, noteId: string): string =>
  `${projectNotesEndpoint(projectId)}/${encodeURIComponent(noteId)}`;
const projectSchedulesEndpoint = (projectId: string): string =>
  `${projectEndpoint(projectId)}/schedules`;
const projectSchedulesGenerateEndpoint = (projectId: string): string =>
  `${projectSchedulesEndpoint(projectId)}/generate`;
const projectScheduleEndpoint = (projectId: string, entryId: string): string =>
  `${projectSchedulesEndpoint(projectId)}/${encodeURIComponent(entryId)}`;
const projectCompareMatrixCellEndpoint = (projectId: string): string =>
  `${projectEndpoint(projectId)}/compare-matrix/cell`;

export class ProjectsApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly payload: unknown;

  constructor(response: Response, payload: unknown) {
    super(apiErrorMessage(response, payload));
    this.name = "ProjectsApiError";
    this.status = response.status;
    this.code = apiErrorCode(payload);
    this.payload = payload;
  }
}

async function requestJson<T>(input: RequestInfo | URL, init: RequestInit): Promise<T> {
  const headers = await withAuthHeaders(init.headers);
  const response = await fetch(input, { ...init, headers });
  const payload = await readJsonPayload(response);

  if (!response.ok) {
    throw new ProjectsApiError(response, payload);
  }

  return payload as T;
}

async function readJsonPayload(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch (error) {
    throw new Error(
      `Projects API returned invalid JSON (HTTP ${response.status}): ${
        error instanceof Error ? error.message : "unknown parse error"
      }`
    );
  }
}

function apiErrorMessage(response: Response, payload: unknown): string {
  const record =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : undefined;
  const serverMessage =
    typeof record?.error === "string"
      ? record.error
      : typeof record?.message === "string"
        ? record.message
        : undefined;

  return serverMessage
    ? `Projects API request failed (HTTP ${response.status}): ${serverMessage}`
    : `Projects API request failed (HTTP ${response.status}).`;
}

function apiErrorCode(payload: unknown): string | undefined {
  const record =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : undefined;

  return typeof record?.code === "string" ? record.code : undefined;
}
